import React, { useState } from "react";
import pbkdf2 from "pbkdf2";
import sss from "shamirs-secret-sharing";
import { Grid, Typography } from "@mui/material";
import TextInput from "components/TextInput";
import Button from "components/NewButton";
import { useLocation, useNavigate } from "react-router-dom";
import AuthHeader from "components/AuthHeader";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  createEVMAccount,
  createEVMSmartAccount,
  decryptMessage,
  encryptMessage,
  fetchAccountNativeBalance,
  getEnsFromAddress,
  showAlert,
  updateEncryptedKeys,
} from "utils/utils";
import {
  setHashedPassword,
  setHashedPin,
  setRecoveryCode,
  setTorusKey,
} from "@slices/walletSlice";
import { initalizeBlockNumbers } from "utils/blocknumber";
import {
  setAccounts,
  setActiveAccount,
  setBlockNumbers,
  setEncryptedSeedPhraseWithPassword,
  setExpirationTime,
  setHoldings,
  setMode,
  setRootAccountInfo,
} from "@slices/appSlice";
import { Accounts } from "interfaces";
import {
  LOGIN_EXPIRY,
  SHARES,
  THRESHOLD,
  stepsCount,
} from "../../../../../../src/constants";
import { Box } from "@mui/system";
import CustomizedSteppers from "components/Stepper";

const RecoverCreatePassword = () => {
  const [securePassword, setSecurePassword] = useState(true);
  const [secureConfirmPassword, setSecureConfirmPassword] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();

  const { state } = location || {};
  const { currentStep, pathName } = state || {};

  const [step, setStep] = useState(parseInt(currentStep) || 0);

  const {
    activeNetwork,
    user: { email },
    accessToken,
    fetchedKeys: { key1, key2, key3, key4, rootAddress },
  } = useAppSelector((state) => state.app);

  const { hashedPin, torusKey, recoveryCode } = useAppSelector(
    (state) => state.wallet
  );

  const fromStringToBuffer = (val) => Buffer.from(val, "hex");
  const bufferToReadable = (buffer) => {
    return buffer.toString("hex");
  };

  const fetchAndSaveData = async (
    address: string,
    privateKey: string,
    newAccount: Accounts,
    counterfactual: string
  ) => {
    dispatch(setExpirationTime(new Date().getTime() + LOGIN_EXPIRY));
    const { balance, balanceInUsd, nativeTokenPrice } =
      await fetchAccountNativeBalance(address, activeNetwork);

    dispatch(
      setHoldings({
        [counterfactual]: {
          nativeBalance: balance,
          nativeBalanceUsd: balanceInUsd,
          nativeTokenPrice,
          tokens: [],
        },
      })
    );

    dispatch(setAccounts(newAccount));
    dispatch(
      setActiveAccount({
        address,
        secret: privateKey,
        smartAccountAddress: counterfactual,
        accountName: newAccount[address]?.name,
      })
    );
    setStep(4);
    navigate("/recover-download-recovery-key", {
      state: {
        currentStep: step + 1,
        pathName: pathName,
      },
    });
  };

  const handlePasswordContinue = async () => {
    try {
      const regex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-_=#$@!%&*?])[A-Za-z\d-_=#$@!%&*?]{8,30}$/;
      setLoading(true);
      if (password !== confirmPassword) {
        showAlert(
          "The passwords you entered do not match. Please check and try again"
        );

        setLoading(false);
      } else {
        if (password.length < 8) {
          showAlert("Passwords must be 8 characters long");

          setLoading(false);
        } else if (!regex.test(password)) {
          showAlert(
            "Password must include numbers, lowercase letters, uppercase letters and special characters"
          );

          setLoading(false);
        } else {
          const hashedPassword = pbkdf2
            .pbkdf2Sync(password, "salt", 1, 32, "sha512")
            .toString("hex");

          const hashedTorus = pbkdf2
            .pbkdf2Sync(torusKey, "salt", 1, 32, "sha512")
            .toString("hex");

          dispatch(setHashedPassword(hashedPassword));
          dispatch(setTorusKey(hashedTorus));

          const decryptedKey1 = decryptMessage(key1, hashedTorus);
          const decryptedKey2 = decryptMessage(key4, recoveryCode);
          const decryptedKey3 = decryptMessage(key3, hashedPin);

          console.log(decryptedKey1, decryptedKey2, decryptedKey3);
          const decryptMnemonic = sss
            .combine([
              fromStringToBuffer(decryptedKey1),
              fromStringToBuffer(decryptedKey2),
              fromStringToBuffer(decryptedKey3),
            ])
            .toString();

          console.log(decryptMnemonic);

          const secretBuffer = Buffer.from(decryptMnemonic);
          const generatedShares = sss.split(secretBuffer, {
            shares: SHARES,
            threshold: THRESHOLD,
          });

          let s = generatedShares.map((s) => bufferToReadable(s));

          const encryptedShare1 = encryptMessage(s[0], hashedTorus);
          const encryptedShare2 = encryptMessage(s[1], hashedPassword);
          const encryptedShare3 = encryptMessage(s[2], hashedPin);

          const updatedRecoveryCode = hashedTorus + hashedPassword + hashedPin;

          const encryptedShare4 = encryptMessage(s[3], updatedRecoveryCode);

          await updateEncryptedKeys(
            email,
            encryptedShare1,
            encryptedShare2,
            encryptedShare3,
            encryptedShare4,
            accessToken
          );

          const encryptedSeedPhraseWithPassword = encryptMessage(
            decryptMnemonic,
            hashedPassword
          );

          const ens = await getEnsFromAddress(rootAddress?.toLowerCase());

          const blockNumbers = await initalizeBlockNumbers();

          dispatch(setBlockNumbers(blockNumbers));

          const { newAccount, address, secret, counterfactual } =
            await createEVMSmartAccount(
              decryptMnemonic,
              1,
              "",
              hashedPassword,
              activeNetwork
            );

          const { address: derivedRootAddress, secret: rootSecret } =
            await createEVMAccount(decryptMnemonic, 0, ens, hashedPassword);

          dispatch(
            setEncryptedSeedPhraseWithPassword(encryptedSeedPhraseWithPassword)
          );

          dispatch(setMode("google"));
          if (rootAddress.toLowerCase() === derivedRootAddress.toLowerCase()) {
            dispatch(
              setRootAccountInfo({
                address: rootAddress,
                name: ens,
                secret: rootSecret,
              })
            );
          }

          await fetchAndSaveData(address, secret, newAccount, counterfactual);

          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleKeyPress = (e: any) => {
    const { key, keyCode } = e || {};
    if (key === "Enter" || keyCode === 13 || key === "Tab" || keyCode === 9) {
      e.preventDefault(); // Prevent the default behavior of the Tab key
      handlePasswordContinue();
    }
  };

  const totalSteps = stepsCount[pathName] || 3;

  return (
    <div className="create-account-container">
      <Grid container spacing={2}>
        <Grid item xs={8} className="auth-header-with-steps">
          <AuthHeader />
        </Grid>
        <Grid
          item
          xs={16}
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Box
            className="auth-header-steps"
            width={"50%"}
            style={{
              position: "absolute",
              top: "54px",
            }}
          >
            <CustomizedSteppers
              step={step}
              steps={new Array(totalSteps).fill("")}
              changeStep={setStep}
            />
          </Box>
        </Grid>
      </Grid>
      <div className="create-account-box">
        <Typography
          variant="h2"
          sx={{
            margin: "30px 0px 30px 0px",

            fontWeight: "700",
          }}
        >
          Create your password
        </Typography>

        <div className="welcome-box-container">
          <TextInput
            title="Enter Password"
            value={password}
            onChange={setPassword}
            onKeyPress={(e) => handleKeyPress(e)}
            showSecure={() => setSecurePassword(!securePassword)}
            showEndIcon
            secure={securePassword}
            type={securePassword ? "password" : "text"}
          />
          <TextInput
            title="Confirm Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            showSecure={() => setSecureConfirmPassword(!secureConfirmPassword)}
            showEndIcon
            secure={secureConfirmPassword}
            type={secureConfirmPassword ? "password" : "text"}
            onKeyPress={handleKeyPress}
          />

          <Button
            onClick={handlePasswordContinue}
            title="Continue"
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default RecoverCreatePassword;
